/**
 * Bienvenida de primera conexión — se muestra UNA sola vez.
 *
 * Origen: informe UX de Melissa Amaya (11/09/2026), hallazgos 1, 4 y 7
 * («Ausencia de onboarding», severidad alta, recurrente en dos pantallas: en
 * ningún punto del flujo se explica qué hace cada sección ni cómo empezar).
 * Santiago lo pidió en la reunión del 15/09 con el alcance exacto: explicar
 * qué botón es para hablar, cuál para escribir y cuál para la configuración.
 *
 * El hallazgo 11 del mismo informe (sistema de recompensas invisible) se cubre
 * con la tarjeta del cofre, que solo aparece cuando la gamificación está
 * activa — es decir, en el build de desarrollo. En producción no se menciona,
 * porque el paquete de trofeos sigue sin aprobación de la Alianza.
 */

import { useEffect, useRef } from 'react';
import './WelcomeGuide.css';

interface WelcomeGuideProps {
  /** Modo profesor (aide-prof): textos en francés. Por defecto español. */
  aideProf?: boolean;
  /** Mostrar la tarjeta del cofre de trofeos (solo build de desarrollo). */
  showTrophies?: boolean;
  onClose: () => void;
}

const IconMic = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
  </svg>
);

const IconKeyboard = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const IconGear = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6h.09A1.65 1.65 0 0 0 10.6 3V3a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const IconTrophy = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z" />
    <path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" />
  </svg>
);

const TEXTS = {
  es: {
    title: '¡Hola! Soy Galia',
    intro: 'Practicamos francés conversando. Así funciona:',
    mic: { t: 'Hablar', d: 'Toca el micrófono y háblame en francés. Te respondo con voz y texto.' },
    chat: { t: 'Escribir', d: 'Si prefieres teclear, abre el chat y escríbeme.' },
    gear: { t: 'Configuración', d: 'Cambia tu nivel, mi personalidad y si quiero hablarte o solo escribirte.' },
    trophy: { t: 'Logros', d: 'Vas ganando trofeos a medida que practicas. Míralos en el cofre.' },
    cta: 'Empezar',
  },
  fr: {
    title: 'Bonjour ! Je suis Galia',
    intro: 'Je t’aide à préparer tes cours. Voici comment ça marche :',
    mic: { t: 'Parler', d: 'Appuie sur le micro et parle-moi. Je réponds par la voix et par écrit.' },
    chat: { t: 'Écrire', d: 'Si tu préfères taper, ouvre le chat et écris-moi.' },
    gear: { t: 'Configuration', d: 'Coupe ou active ma voix, et passe en mode conversation quand tu veux.' },
    trophy: { t: 'Trophées', d: 'Tes élèves gagnent des trophées en pratiquant.' },
    cta: 'Commencer',
  },
};

export function WelcomeGuide({ aideProf = false, showTrophies = false, onClose }: WelcomeGuideProps) {
  const t = aideProf ? TEXTS.fr : TEXTS.es;
  const ctaRef = useRef<HTMLButtonElement>(null);

  // Foco en el botón al abrir y cierre con Escape: es un modal, y el informe
  // UX marca «control y libertad del usuario» como el problema más grave.
  useEffect(() => {
    ctaRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const items = [
    { icon: <IconMic />, ...t.mic },
    { icon: <IconKeyboard />, ...t.chat },
    { icon: <IconGear />, ...t.gear },
    ...(showTrophies ? [{ icon: <IconTrophy />, ...t.trophy }] : []),
  ];

  return (
    <div
      className="welcome-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="welcome-card">
        <img
          src="/assets/ui/avatares/Galia_.png"
          alt=""
          className="welcome-avatar"
          draggable={false}
        />
        <h2 className="welcome-title" id="welcome-title">{t.title}</h2>
        <p className="welcome-intro">{t.intro}</p>

        <ul className="welcome-list">
          {items.map((it) => (
            <li className="welcome-item" key={it.t}>
              <span className="welcome-item-icon">{it.icon}</span>
              <span className="welcome-item-body">
                <strong>{it.t}</strong>
                <span>{it.d}</span>
              </span>
            </li>
          ))}
        </ul>

        <button ref={ctaRef} type="button" className="welcome-cta" onClick={onClose}>
          {t.cta}
        </button>
      </div>
    </div>
  );
}

export default WelcomeGuide;
